import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { extraBoxLine, majorityFlavor, syncExtraUnit } from '@theme/extra-box';
import {
  bonusLineProperties,
  linkBonusBundle,
  perBoxCentsFromTotal,
  subscriptionCartBody,
  syncFreeBoxOffer,
} from '@theme/free-box-offer';
import { fetchConfig } from '@theme/utilities';

const SKIO_BOX_ENDPOINT = 'https://api.skio.com/storefront-http/get-classic-box-v2';
const DEFAULT_CURRENCY_DECIMALS = 2;
const CURRENCY_DECIMALS = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
};

/**
 * Fixed-pack buy box: in-place flavor/variety swap, native pack-size variants, Skio ATC.
 *
 * @typedef {object} CatalogVariant
 * @property {number | null} id
 * @property {string} [gid]
 * @property {string} title
 * @property {number} size
 * @property {string} [subtitle]
 * @property {number} price
 * @property {number} compare_at_price
 * @property {boolean} available
 *
 * @typedef {object} CatalogPlan
 * @property {number} id
 * @property {string} name
 * @property {string} [frequency]
 * @property {string} [value_type]
 * @property {number} [value]
 *
 * @typedef {object} CatalogMedia
 * @property {string} src
 * @property {string} thumb
 * @property {string} alt
 *
 * @typedef {object} CatalogFlavor
 * @property {string} key
 * @property {string} label
 * @property {string} title
 * @property {string} description
 * @property {string} image
 * @property {string} handle
 * @property {string} url
 * @property {boolean} isVariety
 * @property {boolean} isBuilder
 * @property {boolean} hasProduct
 * @property {CatalogMedia[]} media
 * @property {CatalogVariant[]} variants
 * @property {number | null} [oneBoxId]
 * @property {CatalogPlan[]} sellingPlans
 *
 * @typedef {object} CatalogInside
 * @property {string} title
 * @property {string} image
 *
 * @typedef {object} CatalogSettings
 * @property {string} sizeLabel
 * @property {string} flavorLabel
 * @property {string} insideLabel
 * @property {string} ctaLabel
 * @property {string} ctaDisabledLabel
 * @property {string} ctaNote
 * @property {string} stickyCta
 * @property {string} [stickySelectLabel]
 * @property {string} [notifyLabel]
 * @property {string} [notifyPlaceholder]
 * @property {string} [notifySuccess]
 * @property {string} [mixAddLabel]
 * @property {string} [mixSoldOutLabel]
 * @property {string} [ladderUnlock]
 * @property {string} [ladderAddMore]
 * @property {string} subscribeLabel
 * @property {string} oneTimeLabel
 * @property {string} mostPopular
 * @property {string} frequencyPrefix
 * @property {number} defaultFlavorSize
 * @property {number} defaultVarietySize
 *
 * @typedef {object} Catalog
 * @property {CatalogFlavor[]} flavors
 * @property {CatalogInside[]} inside
 * @property {CatalogSettings} settings
 *
 * @typedef {object} FixedPackBuyboxRefs
 * @property {HTMLScriptElement} catalog
 * @property {HTMLTemplateElement} moneyFormat
 * @property {HTMLElement} gallerySwiper
 * @property {HTMLElement} gallerySlides
 * @property {HTMLElement} thumbsSwiper
 * @property {HTMLElement} thumbs
 * @property {HTMLElement} title
 * @property {HTMLElement} [mobilePrice]
 * @property {HTMLElement} description
 * @property {HTMLElement} sizeRow
 * @property {HTMLElement} [ladder]
 * @property {HTMLElement} [trialNote]
 * @property {HTMLElement} [purchase]
 * @property {HTMLTemplateElement} [ladderTruckIcon]
 * @property {HTMLTemplateElement} [ladderGiftIcon]
 * @property {HTMLElement} [inside]
 * @property {HTMLElement} [insideCount]
 * @property {HTMLElement} [insideList]
 * @property {HTMLElement} [insideRows]
 * @property {HTMLElement} [subscribeCard]
 * @property {HTMLElement} [oneTimeCard]
 * @property {HTMLInputElement} [subscribeRadio]
 * @property {HTMLInputElement} [oneTimeRadio]
 * @property {HTMLElement} [subscribePrice]
 * @property {HTMLElement} [subscribeCompare]
 * @property {HTMLElement} [saveBadge]
 * @property {HTMLElement} [frequencyPrefix]
 * @property {HTMLSelectElement} [frequencySelect]
 * @property {HTMLElement} [oneTimePrice]
 * @property {HTMLButtonElement} atcButton
 * @property {HTMLElement} atcLabel
 * @property {HTMLElement} [atcError]
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement} [sticky]
 * @property {HTMLImageElement} [stickyThumb]
 * @property {HTMLElement} [stickyCompare]
 * @property {HTMLElement} [stickyPrice]
 * @property {HTMLElement} [stickyTitle]
 * @property {HTMLElement} [stickyMeta]
 * @property {HTMLElement} [stickyEach]
 * @property {HTMLButtonElement} [stickyButton]
 * @property {HTMLElement} [stickyFlavors]
 * @property {HTMLButtonElement} [stickySelect]
 * @property {HTMLElement} [stickyFlavorList]
 *
 * @extends {Component<FixedPackBuyboxRefs>}
 */
class CtrFixedPackBuybox extends Component {
  requiredRefs = ['catalog', 'moneyFormat', 'gallerySwiper', 'gallerySlides', 'thumbsSwiper', 'thumbs', 'title', 'description', 'sizeRow', 'atcButton', 'atcLabel', 'liveRegion'];

  /** @type {Catalog | null} */
  #catalog = null;

  /** @type {string} */
  #flavorKey = '';

  /** @type {number} */
  #size = 0;

  /** @type {boolean} */
  #subscribe = true;
  /** @type {MutationObserver | null} */
  #halloweenPriceObserver = null;

  /** @type {boolean} */
  #forcedOneTime = false;

  /** @type {string} */
  #extraGid = '';

  /** @type {boolean} */
  #extraTouched = false;

  /** @type {string} */
  #extraFlavorKey = '';

  /** @type {number | null} */
  #planId = null;

  /** @type {number} */
  #mediaIndex = 0;

  /** @type {{ destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void, slideTo: (index: number, speed?: number) => void, update: () => void, on: (event: string, handler: Function) => void, activeIndex?: number } | undefined} */
  #mainSwiper;

  /** @type {{ destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void, update: () => void } | undefined} */
  #thumbsInstance;

  /** @type {number} */
  #swiperToken = 0;

  /** @type {IntersectionObserver | undefined} */
  #stickyObserver;

  /** @type {ResizeObserver | undefined} */
  #stickyResizeObserver;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {boolean} */
  #hasRendered = false;

  /** @type {boolean} */
  #wasVariety = false;

  /** @type {((event: TransitionEvent) => void) | undefined} */
  #descriptionTransitionEnd;

  /** @type {number | undefined} */
  #descriptionTimer;

  /** @type {Map<string, number>} */
  #mixSelections = new Map();

  /** @type {Map<string, { platformId: string, productTitle: string, productVariantTitle: string, outOfStock?: boolean }>} */
  #mixMeta = new Map();

  /** @type {Map<string, Promise<{ platformId: string, size: number, selectableProductVariants: Array<{ platformId: string, productTitle: string, productVariantTitle: string, imageSrc?: string, outOfStock?: boolean }> }>>} */
  #boxCache = new Map();

  /** @type {'idle' | 'loading' | 'ready' | 'error'} */
  #mixStatus = 'idle';

  /** @type {boolean} */
  #notifyOpen = false;

  /** @type {boolean} */
  #stickyFlavorsOpen = false;

  connectedCallback() {
    super.connectedCallback();
    this.#catalog = this.#readCatalog();
    if (!this.#catalog?.flavors?.length) return;

    const currentHandle = this.dataset.productHandle || '';
    const matched = currentHandle
      ? this.#catalog.flavors.find((item) => item.handle === currentHandle)
      : undefined;
    const first =
      matched ||
      this.#catalog.flavors.find((item) => !item.isVariety && !item.isBuilder) ||
      this.#catalog.flavors[0];
    this.#flavorKey = first.key;
    this.#size = this.#defaultSize(first);
    this.#subscribe = Boolean(first.sellingPlans?.length);
    this.#planId = first.sellingPlans?.[0]?.id ?? null;
    this.#mediaIndex = 0;
    this.#notifyOpen = Boolean(this.querySelector('[data-notify-success]'));

    this.#render();
    if (this.#isBuilderMode()) this.#initMix();
    this.#observeSticky();
    this.#syncFooterOffset();
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroySwipers();
    this.#halloweenPriceObserver?.disconnect();
    this.#halloweenPriceObserver = null;
    this.#stickyObserver?.disconnect();
    this.#stickyResizeObserver?.disconnect();
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#clearDescriptionAnimation();
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    this.#clearFooterOffset();
  }

  /**
   * @param {Event} event
   */
  selectFlavor(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-flavor-key]') : null;
    if (!(button instanceof HTMLElement)) return;
    const key = button.dataset.flavorKey;
    const flavor = this.#flavorByKey(key);
    if (!flavor) return;

    if (flavor.isBuilder && flavor.url && flavor.handle !== (this.dataset.productHandle || '')) {
      window.location.assign(flavor.url);
      return;
    }

    const layout = this.dataset.flavorLayout || 'all';
    const currentHandle = this.dataset.productHandle || '';
    if (layout === 'pdp_nav' && flavor.url && flavor.handle && flavor.handle !== currentHandle) {
      window.location.assign(flavor.url);
      return;
    }

    this.#flavorKey = flavor.key;
    this.#size = this.#closestSize(flavor, this.#size) || this.#defaultSize(flavor);
    if (!flavor.sellingPlans?.length) {
      this.#subscribe = false;
      this.#planId = null;
    } else if (this.#subscribe) {
      this.#planId = this.#planId && flavor.sellingPlans.some((plan) => plan.id === this.#planId)
        ? this.#planId
        : flavor.sellingPlans[0].id;
    }
    this.#mediaIndex = 0;
    this.#render();
  }

  /**
   * @param {Event} event
   */
  selectSize(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-size]') : null;
    if (!(button instanceof HTMLElement)) return;
    const size = Number(button.dataset.size);
    if (!size) return;
    this.#size = size;
    if (this.#isBuilderMode()) this.#truncateMix();
    this.#render({ skipGallery: true });
  }

  /**
   * @param {Event} event
   */
  selectPurchase(event) {
    const input = event.target instanceof Element ? event.target.closest('[data-purchase]') : null;
    if (!(input instanceof HTMLInputElement)) return;
    this.#subscribe = input.value === 'subscribe';
    const flavor = this.#activeFlavor();
    if (this.#subscribe) {
      this.#planId = this.#planId && flavor?.sellingPlans.some((plan) => plan.id === this.#planId)
        ? this.#planId
        : flavor?.sellingPlans[0]?.id ?? null;
    } else {
      this.#planId = null;
    }
    this.#render({ skipGallery: true });
  }

  /**
   * @param {Event} event
   */
  changeFrequency(event) {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select) return;
    const id = Number(select.value);
    this.#planId = id || null;
    this.#subscribe = Boolean(id);
    this.#render({ skipGallery: true });
  }

  /**
   * Keep frequency clicks on the select instead of the parent plan label.
   * @param {Event} event
   */
  keepFrequencyClick(event) {
    event.stopPropagation();
  }

  /**
   * @param {Event} event
   */
  incrementMix(event) {
    const gid = this.#mixRowGid(event);
    if (!gid || this.#mixStatus !== 'ready') return;
    if (this.#totalMix() >= this.#size) return;
    if (this.#mixMeta.get(gid)?.outOfStock) return;

    this.#mixSelections.set(gid, (this.#mixSelections.get(gid) ?? 0) + 1);
    this.#updateMixUI();
    this.#updateAtcState();
    this.#updateSticky();
    this.#syncLadderHint(this.#activeFlavor());
  }

  /**
   * @param {Event} event
   */
  decrementMix(event) {
    const gid = this.#mixRowGid(event);
    if (!gid) return;

    const qty = (this.#mixSelections.get(gid) ?? 0) - 1;
    if (qty <= 0) this.#mixSelections.delete(gid);
    else this.#mixSelections.set(gid, qty);

    this.#updateMixUI();
    this.#updateAtcState();
    this.#updateSticky();
    this.#syncLadderHint(this.#activeFlavor());
  }

  retryMix() {
    this.#boxCache.clear();
    this.#initMix();
  }

  openNotify() {
    this.#notifyOpen = true;
    const panel = this.refs.notifyPanel;
    if (panel) panel.hidden = false;
    if (this.refs.atcButton) this.refs.atcButton.hidden = true;
    this.refs.notifyEmail?.focus();
    this.refs.notifyPanel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  scrollToMix() {
    const mix = this.refs.mixGrid;
    if (mix instanceof HTMLElement) {
      mix.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    this.querySelector('.ctr-fp-buybox__flavors')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * @param {Event} event
   */
  toggleStickyFlavors(event) {
    event.stopPropagation();
    this.#setStickyFlavorsOpen(!this.#stickyFlavorsOpen);
  }

  /**
   * @param {Event} event
   */
  selectThumb(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-media-index]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#mediaIndex = Number(button.dataset.mediaIndex) || 0;
    this.#syncThumbSelection();
    this.#mainSwiper?.slideTo(this.#mediaIndex, 250);
    this.#updateStickyThumb();
  }

  async addToCart() {
    const flavor = this.#activeFlavor();
    if (!flavor) return;

    if (this.#isNotifyMode(flavor)) {
      this.openNotify();
      return;
    }

    if (this.#isBuilderMode()) {
      await this.#addConfiguredBox();
      return;
    }

    const variant = this.#activeVariant();
    if (!variant?.id || !flavor.hasProduct) return;

    const { atcButton, stickyButton, atcError, liveRegion } = this.refs;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const line = {
      id: variant.id,
      quantity: 1,
    };
    const scheduleOffer = Boolean(this.querySelector('[data-free-box-offer]'));
    const includeFree = Boolean(scheduleOffer && this.#subscribe && this.#planId);
    if (this.#subscribe && this.#planId) line.selling_plan = this.#planId;
    const bonusProperties = scheduleOffer && this.#subscribe ? bonusLineProperties(this) : {};
    if (Object.keys(bonusProperties).length) line.properties = bonusProperties;
    const payload = subscriptionCartBody(line, this, includeFree, sectionIds.join(','), window.location.pathname);
    this.#appendExtraLine(payload, line.id);

    atcButton.disabled = true;
    atcButton.setAttribute('aria-busy', 'true');
    if (stickyButton) {
      stickyButton.disabled = true;
      stickyButton.setAttribute('aria-busy', 'true');
    }
    if (atcError) atcError.hidden = true;

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify(payload) })
      ).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
        if (atcError) {
          atcError.textContent = response.message;
          atcError.hidden = false;
        }
        liveRegion.textContent = response.message;
        this.#errorTimeout = setTimeout(() => {
          if (atcError) atcError.hidden = true;
          liveRegion.textContent = '';
        }, 10000);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(variant.id), {
          source: 'ctr-fixed-pack-buybox',
          itemCount: payload.items.length,
          productId: String(flavor.key),
          variantId: String(variant.id),
          sections: response.sections,
        })
      );
      liveRegion.textContent = Theme.translations?.added ?? 'Added to cart';
    } catch (error) {
      console.error(error);
      if (atcError) {
        atcError.textContent = 'Something went wrong. Please try again.';
        atcError.hidden = false;
      }
    } finally {
      atcButton.removeAttribute('aria-busy');
      if (stickyButton) stickyButton.removeAttribute('aria-busy');
      this.#updateAtcState();
    }
  }

  /** @returns {Catalog | null} */
  #readCatalog() {
    try {
      return JSON.parse(this.refs.catalog.textContent || 'null');
    } catch (error) {
      console.error('[ctr-fixed-pack-buybox] Invalid catalog JSON', error);
      return null;
    }
  }

  /** @param {string | undefined} key */
  #flavorByKey(key) {
    return this.#catalog?.flavors.find((item) => item.key === key);
  }

  #activeFlavor() {
    return this.#flavorByKey(this.#flavorKey);
  }

  #activeVariant() {
    const flavor = this.#activeFlavor();
    if (!flavor) return undefined;
    return flavor.variants.find((item) => item.size === this.#size) || flavor.variants[0];
  }

  /**
   * Sold out when this flavor has the currently selected pack size and that variant cannot be purchased.
   * Flavors that do not offer the selected size (e.g. variety at 6 boxes) are not marked sold out.
   *
   * @param {CatalogFlavor} flavor
   */
  #isSoldOutForSelectedSize(flavor) {
    if (flavor.isBuilder || !flavor.hasProduct) return false;
    const variant = flavor.variants.find((item) => item.size === this.#size);
    return Boolean(variant && !variant.available);
  }

  #activePlan() {
    const flavor = this.#activeFlavor();
    if (!flavor || !this.#planId) return undefined;
    return flavor.sellingPlans.find((plan) => plan.id === this.#planId);
  }

  /** @param {CatalogFlavor} flavor */
  #defaultSize(flavor) {
    const preferred = flavor.isVariety
      ? this.#catalog?.settings.defaultVarietySize || 8
      : this.#catalog?.settings.defaultFlavorSize || 6;
    return this.#closestSize(flavor, preferred);
  }

  /**
   * @param {CatalogFlavor} flavor
   * @param {number} size
   */
  #closestSize(flavor, size) {
    if (flavor.variants.some((item) => item.size === size)) return size;
    const sorted = [...flavor.variants].sort((a, b) => a.size - b.size);
    if (!sorted.length) return 0;
    const larger = sorted.find((item) => item.size >= size);
    return (larger || sorted[sorted.length - 1]).size;
  }

  /**
   * @param {{ skipGallery?: boolean }} [options]
   */
  #render(options = {}) {
    const flavor = this.#activeFlavor();
    if (!flavor) return;

    const modeChanged = this.#hasRendered && this.#wasVariety !== flavor.isVariety;
    const animateDescription = this.#hasRendered && !options.skipGallery && !modeChanged;

    const mode = flavor.isBuilder ? 'builder' : flavor.isVariety ? 'variety' : 'flavor';
    const notify = this.#isNotifyMode(flavor);
    this.dataset.mode = mode;
    this.dataset.notify = notify ? 'true' : 'false';
    this.refs.title.textContent = flavor.title;
    this.#setDescription(flavor.description, animateDescription);

    this.querySelectorAll('[data-flavor-key]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const option = this.#flavorByKey(node.dataset.flavorKey);
      const selected = node.dataset.flavorKey === flavor.key;
      const soldOut = option ? this.#isSoldOutForSelectedSize(option) : false;
      node.classList.toggle('is-selected', selected);
      node.classList.toggle('is-sold-out', soldOut);
      node.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.#renderSizes(flavor);
    this.#renderLadder(flavor);
    this.#renderInside(flavor);
    this.#renderPurchase(flavor);
    this.#renderNotify(flavor);
    if (this.#isBuilderMode()) this.#updateMixUI();
    if (!options.skipGallery) this.#renderGallery();
    this.#updateAtcState();
    this.#updateSticky();

    this.#wasVariety = flavor.isVariety;
    this.#hasRendered = true;
  }

  #clearDescriptionAnimation() {
    if (this.#descriptionTimer) {
      window.clearTimeout(this.#descriptionTimer);
      this.#descriptionTimer = undefined;
    }
    const el = this.refs?.description;
    if (this.#descriptionTransitionEnd && el) {
      el.removeEventListener('transitionend', this.#descriptionTransitionEnd);
    }
    this.#descriptionTransitionEnd = undefined;
  }

  /**
   * Animate description height between flavors. Skipped for variety-pack mode changes
   * so inside/ladder/plan layout can settle without a second card-height jump.
   *
   * @param {string} text
   * @param {boolean} animate
   */
  #setDescription(text, animate) {
    const el = this.refs.description;
    if (!el) return;

    this.#clearDescriptionAnimation();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduceMotion) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      el.textContent = text;
      return;
    }

    const fromHeight = el.getBoundingClientRect().height;
    el.textContent = text;
    el.style.transition = 'none';
    el.style.height = 'auto';
    el.style.overflow = 'hidden';
    const toHeight = el.scrollHeight;

    if (Math.abs(fromHeight - toHeight) < 0.5) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      return;
    }

    el.style.height = `${fromHeight}px`;
    void el.offsetHeight;
    el.style.transition = 'height var(--ctr-fp-ease-height, 0.4s ease)';
    el.style.height = `${toHeight}px`;

    const finish = () => {
      this.#clearDescriptionAnimation();
      el.style.transition = 'none';
      el.style.height = '';
      el.style.overflow = '';
      void el.offsetHeight;
      el.style.transition = '';
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== el || event.propertyName !== 'height') return;
      finish();
    };
    this.#descriptionTransitionEnd = onEnd;
    el.addEventListener('transitionend', onEnd);
    this.#descriptionTimer = window.setTimeout(finish, 500);
  }

  /** @param {CatalogFlavor} flavor */
  #renderSizes(flavor) {
    if (!flavor.variants.length) {
      this.refs.sizeRow.replaceChildren();
      return;
    }

    const nextKeys = flavor.variants.map((item) => String(item.size)).join(',');
    const existing = [...this.refs.sizeRow.querySelectorAll('[data-size]')];
    const existingKeys = existing.map((node) => node.dataset.size).join(',');

    if (existing.length && existingKeys === nextKeys) {
      existing.forEach((button) => this.#applySizeButtonState(button, flavor));
      return;
    }

    const bestSize = Math.max(...flavor.variants.map((item) => item.size));
    this.refs.sizeRow.replaceChildren(
      ...flavor.variants.map((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ctr-fp-buybox__size';
        button.dataset.size = String(variant.size);
        button.setAttribute('on:click', '/selectSize');

        if (variant.size === bestSize && flavor.variants.length > 1) {
          const badge = document.createElement('span');
          badge.className = 'ctr-fp-buybox__size-badge';
          badge.textContent = 'BEST DEAL';
          button.append(badge);
        }

        const sold = document.createElement('span');
        sold.className = 'ctr-fp-buybox__size-sold';
        sold.textContent = 'SOLD OUT';
        button.append(sold);

        const name = document.createElement('span');
        name.className = 'ctr-fp-buybox__size-name';
        name.textContent = variant.title;
        button.append(name);

        if (variant.subtitle) {
          const subtitle = document.createElement('span');
          subtitle.className = 'ctr-fp-buybox__size-sub';
          subtitle.textContent = variant.subtitle;
          button.append(subtitle);
        }

        if (flavor.hasProduct && variant.price > 0) {
          const plan = this.#activePlan() || flavor.sellingPlans?.[0];
          const display = this.#displayCentsForSize(variant, plan);

          const price = document.createElement('span');
          price.className = 'ctr-fp-buybox__size-price';
          price.textContent = `${this.#formatMoney(display)} total`;
          button.append(price);

          const per = document.createElement('span');
          per.className = 'ctr-fp-buybox__size-each';
          per.textContent = `${this.#formatMoney(this.#perBoxCents(display, variant.size))}/box`;
          button.append(per);
        }

        this.#applySizeButtonState(button, flavor);
        return button;
      })
    );
  }

  /**
   * @param {HTMLElement} button
   * @param {CatalogFlavor} flavor
   */
  #applySizeButtonState(button, flavor) {
    const size = Number(button.dataset.size);
    const variant = flavor.variants.find((item) => item.size === size);
    const soldOut = Boolean(flavor.hasProduct && !flavor.isBuilder && variant && !variant.available);
    const selected = size === this.#size;
    button.classList.toggle('is-selected', selected);
    button.classList.toggle('is-sold-out', soldOut);
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');

    if (!button.querySelector('.ctr-fp-buybox__size-sold')) {
      const sold = document.createElement('span');
      sold.className = 'ctr-fp-buybox__size-sold';
      sold.textContent = 'SOLD OUT';
      button.append(sold);
    }
  }

  /** @param {CatalogFlavor} flavor */
  #renderLadder(flavor) {
    const ladder = this.refs.ladder;
    if (!ladder) return;

    const shouldShow = !flavor.isVariety && !this.#isNotifyMode(flavor) && flavor.variants.length >= 2;
    if (shouldShow) {
      const segs = ladder.querySelectorAll('.ctr-fp-buybox__ladder-seg');
      if (segs.length !== flavor.variants.length || !ladder.querySelector('.ctr-fp-buybox__ladder-fill')) {
        this.#buildLadder(flavor);
      }
      this.#syncLadderState(flavor);
    }
    ladder.classList.toggle('is-open', shouldShow);
    ladder.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    this.#syncLadderHint(flavor);
  }

  /** @param {CatalogFlavor} flavor */
  #buildLadder(flavor) {
    const ladder = this.refs.ladder;
    if (!ladder) return;

    const labels = document.createElement('div');
    labels.className = 'ctr-fp-buybox__ladder-labels';
    const track = document.createElement('div');
    track.className = 'ctr-fp-buybox__ladder-track';
    const rail = document.createElement('span');
    rail.className = 'ctr-fp-buybox__ladder-rail';
    const fill = document.createElement('span');
    fill.className = 'ctr-fp-buybox__ladder-fill';
    track.append(rail, fill);
    const prices = document.createElement('div');
    prices.className = 'ctr-fp-buybox__ladder-prices';

    flavor.variants.forEach((variant, index) => {
      const labelCell = document.createElement('div');
      labelCell.className = 'ctr-fp-buybox__ladder-cell';
      if (index === flavor.variants.length - 1) {
        const badge = document.createElement('span');
        badge.className = 'ctr-fp-buybox__ladder-badge';
        badge.textContent = 'BEST DEAL';
        labelCell.append(badge);
      }
      const label = document.createElement('span');
      label.className = 'ctr-fp-buybox__ladder-label';
      const extraOnly = this.#extraBoxOnly();
      label.textContent = extraOnly && variant.size === 4 ? 'Trial Pack (4 Boxes)' : variant.title;
      labelCell.append(label);

      const seg = document.createElement('div');
      seg.className = 'ctr-fp-buybox__ladder-seg';
      const dot = document.createElement('span');
      dot.className = 'ctr-fp-buybox__ladder-dot';
      dot.innerHTML =
        '<svg class="ctr-fp-buybox__ladder-check" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6.4 L4.8 9.2 L10 3.2" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      seg.append(dot);

      const priceCell = document.createElement('div');
      priceCell.className = 'ctr-fp-buybox__ladder-cell';
      const each = variant.size ? this.#formatMoney(this.#ladderEachCents(variant, flavor)) : '';
      const price = document.createElement('span');
      price.className = 'ctr-fp-buybox__ladder-price';
      if (each) {
        price.append(document.createTextNode(`${each}/ea`));
        if (index === flavor.variants.length - 1) {
          price.append(document.createTextNode(' · '));
          const deal = document.createElement('span');
          deal.className = 'ctr-fp-buybox__ladder-deal';
          deal.textContent = 'best deal';
          price.append(deal);
        }
      }
      priceCell.append(price);

      if (this.querySelector('[data-free-box-offer]') || extraOnly) {
        const perks = document.createElement('div');
        perks.className = 'ctr-fp-buybox__ladder-perks';
        if (this.querySelector('[data-free-box-offer]')) {
          perks.append(this.#ladderPerk('+3 Free Boxes', 'gift'));
        } else if (variant.size === 4) {
          perks.append(this.#ladderPerk('One Time Purchase'));
        } else if (variant.size === 6) {
          perks.append(this.#ladderPerk('20% OFF with your subscription', 'tag'));
        } else if (variant.size >= 8) {
          perks.append(this.#ladderPerk('20% OFF with your subscription', 'tag'));
          perks.append(this.#ladderPerk('+1 Free Box', 'gift'));
        }
        if (perks.childElementCount) priceCell.append(perks);
      }

      labels.append(labelCell);
      track.append(seg);
      prices.append(priceCell);
    });

    ladder.replaceChildren(labels, track, prices);
  }

  /**
   * @param {string} label
   * @param {'truck' | 'gift' | 'tag'} [icon]
   * @returns {HTMLSpanElement}
   */
  #ladderPerk(label, icon) {
    const perk = document.createElement('span');
    perk.className =
      icon === 'gift'
        ? 'ctr-fp-buybox__ladder-perk ctr-fp-buybox__ladder-perk--gift'
        : icon === 'tag'
          ? 'ctr-fp-buybox__ladder-perk ctr-fp-buybox__ladder-perk--sale'
          : 'ctr-fp-buybox__ladder-perk';
    const source =
      icon === 'truck'
        ? this.refs.ladderTruckIcon
        : icon === 'gift'
          ? this.refs.ladderGiftIcon
          : icon === 'tag'
            ? this.refs.ladderTagIcon
            : null;
    const graphic = source instanceof HTMLTemplateElement ? source.content.firstElementChild?.cloneNode(true) : null;
    if (graphic) perk.append(graphic);
    if (icon === 'tag') {
      const text = document.createElement('span');
      text.textContent = label;
      perk.append(text);
    } else {
      perk.append(document.createTextNode(label));
    }
    return perk;
  }

  /** @param {CatalogFlavor} flavor */
  #syncLadderState(flavor) {
    const ladder = this.refs.ladder;
    if (!ladder) return;
    const selectedIndex = Math.max(
      0,
      flavor.variants.findIndex((item) => item.size === this.#size)
    );

    const count = flavor.variants.length || 1;
    const atLast = selectedIndex === count - 1;
    const progress = atLast ? 100 : ((selectedIndex + 0.5) / count) * 100;
    const track = ladder.querySelector('.ctr-fp-buybox__ladder-track');
    track?.style.setProperty('--ctr-fp-ladder-progress', `${progress}%`);

    ladder.querySelectorAll('.ctr-fp-buybox__ladder-seg').forEach((seg, index) => {
      seg.classList.toggle('is-active', index <= selectedIndex);
      seg.classList.toggle('is-current', index === selectedIndex);
    });
    ladder.querySelectorAll('.ctr-fp-buybox__ladder-labels .ctr-fp-buybox__ladder-cell').forEach((cell, index) => {
      cell.classList.toggle('is-current', index === selectedIndex);
      cell.classList.toggle('is-muted', index > selectedIndex);
    });
    ladder.querySelectorAll('.ctr-fp-buybox__ladder-prices .ctr-fp-buybox__ladder-cell').forEach((cell, index) => {
      cell.classList.toggle('is-current', index === selectedIndex);
      cell.classList.toggle('is-muted', index > selectedIndex);
    });
  }

  /** @param {CatalogFlavor} flavor */
  #renderInside(flavor) {
    const inside = this.refs.inside;
    if (!inside) return;
    const rows = this.#catalog?.inside || [];
    const shouldShow = Boolean(flavor.isVariety) && rows.length > 0;
    inside.hidden = !shouldShow;
    inside.classList.toggle('is-open', shouldShow);
    inside.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    if (!shouldShow) return;

    const qty = this.#size >= 8 ? 2 : 1;
    if (this.refs.insideCount) {
      this.refs.insideCount.textContent = `${qty * rows.length} Items`;
    }
    inside.querySelectorAll('[data-inside-qty]').forEach((node) => {
      node.textContent = `x ${qty}`;
    });
  }

  /** @param {CatalogFlavor} flavor */
  #renderPurchase(flavor) {
    this.#applyFreeBoxPurchaseRule(flavor);

    const variant = this.#activeVariant();
    const hasPlans = Boolean(flavor.sellingPlans?.length);
    const subscribeCard = this.refs.subscribeCard;
    const oneTimeCard = this.refs.oneTimeCard;

    if (subscribeCard) subscribeCard.hidden = !hasPlans;
    if (!variant) {
      this.#syncFreeBox(flavor);
      return;
    }

    const oneTime = variant.price;
    const plan = this.#activePlan() || flavor.sellingPlans[0];
    const sub = plan ? this.#planPrice(variant, plan) : oneTime;

    if (this.refs.oneTimePrice) this.refs.oneTimePrice.textContent = this.#formatMoney(oneTime);
    if (this.refs.subscribePrice) this.refs.subscribePrice.textContent = this.#formatMoney(sub);
    if (this.refs.subscribeCompare) {
      this.refs.subscribeCompare.textContent = this.#formatMoney(oneTime);
      this.refs.subscribeCompare.hidden = sub >= oneTime;
    }
    if (this.refs.saveBadge) {
      const pct = oneTime > sub ? Math.round(((oneTime - sub) / oneTime) * 100) : 0;
      this.refs.saveBadge.hidden = !pct;
      if (pct) this.refs.saveBadge.textContent = `SAVE ${pct}%`;
    }
    if (this.refs.mobilePrice) {
      this.refs.mobilePrice.textContent = this.#formatMoney(this.#subscribe ? sub : oneTime);
    }

    if (this.refs.subscribeRadio) this.refs.subscribeRadio.checked = this.#subscribe && hasPlans;
    if (this.refs.oneTimeRadio) this.refs.oneTimeRadio.checked = !this.#subscribe || !hasPlans;
    subscribeCard?.classList.toggle('is-selected', this.#subscribe && hasPlans);
    oneTimeCard?.classList.toggle('is-selected', !this.#subscribe || !hasPlans);

    const select = this.refs.frequencySelect;
    if (select && hasPlans) {
      const prefixSetting = (this.#catalog?.settings.frequencyPrefix || 'Refills delivered ').trim();
      const hasEvery = flavor.sellingPlans.some((item) => /every/i.test(item.name));
      const prefixLabel = hasEvery
        ? /every$/i.test(prefixSetting)
          ? `${prefixSetting} `
          : `${prefixSetting} every `
        : `${prefixSetting} `;
      if (this.refs.frequencyPrefix) {
        this.refs.frequencyPrefix.textContent = prefixLabel;
        const row = this.refs.frequencyPrefix.closest('.ctr-fp-buybox__frequency-row');
        if (row instanceof HTMLElement) {
          row.style.setProperty(
            '--fp-freq-pad',
            `${16 + this.refs.frequencyPrefix.offsetWidth + 6}px`
          );
        }
      }
      const nextIds = flavor.sellingPlans.map((item) => String(item.id)).join(',');
      const currentIds = [...select.options].map((item) => item.value).join(',');
      if (currentIds !== nextIds) {
        select.replaceChildren(
          ...flavor.sellingPlans.map((item) => {
            const option = document.createElement('option');
            option.value = String(item.id);
            const everyMatch = String(item.name || '').match(/every\s+(.+)/i);
            option.textContent = everyMatch ? everyMatch[1].trim() : item.name;
            return option;
          })
        );
      }
      select.value = String(this.#planId || flavor.sellingPlans[0].id);
      select.disabled = !this.#subscribe;
      select.setAttribute('aria-hidden', this.#subscribe ? 'false' : 'true');
      select.setAttribute('aria-label', `${prefixLabel}frequency`);
    }

    this.#syncFreeBox(flavor);
    const tierOffer = Boolean(this.querySelector('[data-free-box-offer]') || this.hasAttribute('data-extra-box-offer'));
    const extraOnly = this.#extraBoxOnly();
    const trialPack = Boolean(extraOnly && this.#size === 4);
    if (this.refs.trialNote instanceof HTMLElement) this.refs.trialNote.hidden = !trialPack;
    if (extraOnly && this.refs.subscribeCard) {
      this.refs.subscribeCard.hidden = trialPack || !Boolean(flavor.sellingPlans?.length);
    }
    if (this.refs.purchase instanceof HTMLElement && tierOffer && !extraOnly) {
      this.refs.purchase.hidden = trialPack;
    }
    if (extraOnly) {
      const variant = this.#activeVariant();
      const plan = this.#activePlan() || flavor.sellingPlans?.[0];
      const oneTime = variant?.price || 0;
      const sub = plan && variant ? this.#planPrice(variant, plan) : oneTime;
      syncExtraUnit(this, this.#subscribe ? sub : oneTime, this.#size, (cents) => this.#formatMoney(cents));
    } else {
      syncExtraUnit(this, 0, 0, (cents) => this.#formatMoney(cents));
    }
    this.#syncExtraBox();
    this.#updateSizeButtonPrices(flavor);
  }

  /**
   * @param {CatalogFlavor} flavor
   */
  #updateSizeButtonPrices(flavor) {
    if (!this.querySelector('[data-free-box-offer]')) return;
    this.#paintSizeButtonPrices(flavor);
    this.#paintLadderPrices(flavor);
    this.#watchHalloweenPrices();
  }

  /**
   * Size cards follow the selected purchase option.
   * @param {CatalogVariant} variant
   * @param {CatalogPlan | undefined} plan
   * @returns {number}
   */
  #displayCentsForSize(variant, plan) {
    const oneTime = variant.price;
    const sub = plan ? this.#planPrice(variant, plan) : oneTime;
    return this.#subscribe && plan ? sub : oneTime;
  }

  /**
   * Progress bar uses the floored subscribe amount, including the 4-box step.
   * @param {CatalogVariant} variant
   * @param {CatalogFlavor} flavor
   * @returns {number}
   */
  #ladderEachCents(variant, flavor) {
    const plan = this.#activePlan() || flavor.sellingPlans?.[0];
    const total =
      this.querySelector('[data-free-box-offer]') && plan ? this.#planPrice(variant, plan) : variant.price;
    return this.#perBoxCents(total, variant.size);
  }

  /** @param {CatalogFlavor} flavor */
  #paintSizeButtonPrices(flavor) {
    const plan = this.#activePlan() || flavor.sellingPlans?.[0];
    const row = this.refs.sizeRow;
    if (!(row instanceof HTMLElement)) return;

    for (const button of row.querySelectorAll('[data-size]')) {
      if (!(button instanceof HTMLElement)) continue;
      const size = Number(button.dataset.size);
      const variant = flavor.variants.find((item) => item.size === size);
      if (!variant || !size) continue;

      const display = this.#displayCentsForSize(variant, plan);
      const priceEl = button.querySelector('.ctr-fp-buybox__size-price');
      const eachEl = button.querySelector('.ctr-fp-buybox__size-each');
      const totalText = `${this.#formatMoney(display)} total`;
      const eachText = `${this.#formatMoney(this.#perBoxCents(display, size))}/box`;
      if (priceEl && priceEl.textContent !== totalText) priceEl.textContent = totalText;
      if (eachEl && eachEl.textContent !== eachText) eachEl.textContent = eachText;
    }
  }

  /** @param {CatalogFlavor} flavor */
  #paintLadderPrices(flavor) {
    const ladder = this.refs.ladder;
    if (!(ladder instanceof HTMLElement) || !this.querySelector('[data-free-box-offer]')) return;

    const prices = ladder.querySelectorAll('.ctr-fp-buybox__ladder-prices .ctr-fp-buybox__ladder-price');
    flavor.variants.forEach((variant, index) => {
      const price = prices[index];
      if (!(price instanceof HTMLElement) || !variant.size) return;
      const each = `${this.#formatMoney(this.#ladderEachCents(variant, flavor))}/ea`;
      const isBest = index === flavor.variants.length - 1;
      const expected = isBest ? `${each} · best deal` : each;
      if (price.textContent.replace(/\s+/g, ' ').trim() === expected) return;

      price.replaceChildren(document.createTextNode(isBest ? `${each} · ` : each));
      if (!isBest) return;
      const badge = document.createElement('span');
      badge.className = 'ctr-fp-buybox__ladder-deal';
      badge.textContent = 'best deal';
      price.append(badge);
    });
  }

  /**
   * Intelligems rewrites per-box amounts to rounded subscribe prices.
   * The observer is disconnected before writing so it cannot retrigger itself.
   */
  #watchHalloweenPrices() {
    if (this.#halloweenPriceObserver || !this.querySelector('[data-free-box-offer]')) return;

    const targets = [this.refs.sizeRow, this.refs.ladder, this.refs.ladderHint].filter(
      (node) => node instanceof HTMLElement
    );
    if (!targets.length) return;

    const apply = () => {
      const observer = this.#halloweenPriceObserver;
      if (!observer) return;
      observer.disconnect();
      try {
        const flavor = this.#activeFlavor();
        if (flavor) {
          this.#paintSizeButtonPrices(flavor);
          this.#paintLadderPrices(flavor);
          this.#syncLadderHint(flavor);
        }
      } finally {
        for (const target of targets) {
          observer.observe(target, { childList: true, characterData: true, subtree: true });
        }
      }
    };

    this.#halloweenPriceObserver = new MutationObserver(apply);
    for (const target of targets) {
      this.#halloweenPriceObserver.observe(target, { childList: true, characterData: true, subtree: true });
    }
  }

  /**
   * Extra-box 4-packs are one-time. Leaving that size restores subscribe.
   * @param {CatalogFlavor} flavor
   */
  #applyFreeBoxPurchaseRule(flavor) {
    if (!this.#extraBoxOnly()) return;
    if (this.#size === 4) {
      this.#subscribe = false;
      this.#planId = null;
      this.#forcedOneTime = true;
      return;
    }
    if (!this.#forcedOneTime) return;
    this.#forcedOneTime = false;
    const hasPlans = Boolean(flavor.sellingPlans?.length);
    this.#subscribe = hasPlans;
    this.#planId = hasPlans ? flavor.sellingPlans[0].id : null;
  }

  /**
   * @param {CatalogFlavor} flavor
   */
  #syncFreeBox(flavor) {
    const variant = flavor.variants.find((item) => item.size === this.#size) || flavor.variants[0];
    const plan = this.#activePlan() || flavor.sellingPlans?.[0];
    const boxCents = plan && variant ? this.#planPrice(variant, plan) : variant?.price || 0;
    const unitCents = variant && this.#size ? Math.floor(boxCents / this.#size) : 0;
    const flavors = (this.#catalog?.flavors || [])
      .filter((item) => item.hasProduct && !item.isBuilder && !item.isVariety)
      .map((item) => {
        const sized = item.variants.find((entry) => entry.size === this.#size) || item.variants[0];
        return {
          gid: item.oneBoxId ? `gid://shopify/ProductVariant/${item.oneBoxId}` : '',
          variantId: item.oneBoxId ? String(item.oneBoxId) : '',
          title: item.label || item.title,
          image: item.image,
          available: item.variants.some((entry) => entry.available),
        };
      });

    const offer = syncFreeBoxOffer(this, {
      size: this.#size,
      exactSize: this.#size,
      unitCents,
      formatMoney: (cents) => this.#formatMoney(cents),
      frequencyText: plan?.frequency || plan?.name || '',
      subscribeSelected: this.#subscribe,
      isVariety: Boolean(flavor.isVariety),
      flavors,
    });

    if (offer.purchaseMode === 'one-time' && this.refs.subscribeCard) {
      this.refs.subscribeCard.hidden = true;
    }
  }

  #renderGallery() {
    const flavor = this.#activeFlavor();
    if (!flavor) return;
    const media = (flavor.media || []).filter((item) => item.src);
    if (!media.length && flavor.image) {
      media.push({ src: flavor.image, thumb: flavor.image, alt: flavor.title });
    }
    if (!media.length) return;

    this.#destroySwipers();
    if (this.#mediaIndex >= media.length) this.#mediaIndex = 0;

    this.refs.gallerySlides.replaceChildren(
      ...media.map((item) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        const img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || flavor.title;
        img.width = 800;
        img.height = 800;
        slide.append(img);
        return slide;
      })
    );

    this.refs.thumbs.replaceChildren(
      ...media.map((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ctr-fp-buybox__thumb';
        button.dataset.mediaIndex = String(index);
        button.setAttribute('on:click', '/selectThumb');
        button.setAttribute('aria-pressed', 'false');
        const img = document.createElement('img');
        img.src = item.thumb || item.src;
        img.alt = item.alt || '';
        button.append(img);
        slide.append(button);
        return slide;
      })
    );

    this.#updateStickyThumb();
    this.#syncThumbSelection();
    this.#initSwipers(media.length);
  }

  #syncThumbSelection() {
    this.refs.thumbs?.querySelectorAll('.ctr-fp-buybox__thumb').forEach((button) => {
      const selected = Number(button.dataset.mediaIndex) === this.#mediaIndex;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  /** @param {number} slideCount */
  #initSwipers(slideCount) {
    const token = ++this.#swiperToken;
    const start = () => {
      if (token !== this.#swiperToken) return true;
      const SwiperCtor = typeof window !== 'undefined' ? window.Swiper : undefined;
      if (typeof SwiperCtor !== 'function' || slideCount < 1) return false;

      this.#thumbsInstance = new SwiperCtor(this.refs.thumbsSwiper, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchSlidesProgress: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        a11y: { containerRole: 'region', containerMessage: 'Product image thumbnails' },
      });

      this.#mainSwiper = new SwiperCtor(this.refs.gallerySwiper, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: this.#mediaIndex,
        watchOverflow: true,
        thumbs: { swiper: this.#thumbsInstance },
        a11y: { containerRole: 'region', containerMessage: 'Product images' },
      });

      this.#mainSwiper.on('slideChange', () => {
        this.#mediaIndex = this.#mainSwiper?.activeIndex || 0;
        this.#syncThumbSelection();
        this.#updateStickyThumb();
      });
      this.#syncThumbSelection();
      return true;
    };

    if (start()) return;
    const startedAt = Date.now();
    const poll = () => {
      if (start() || Date.now() - startedAt > 4000) return;
      requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  }

  #destroySwipers() {
    this.#swiperToken += 1;
    this.#mainSwiper?.destroy(true, true);
    this.#thumbsInstance?.destroy(true, true);
    this.#mainSwiper = undefined;
    this.#thumbsInstance = undefined;
  }

  #updateStickyThumb() {
    const flavor = this.#activeFlavor();
    if (!flavor || !this.refs.stickyThumb) return;
    const media = (flavor.media || []).filter((item) => item.src);
    const current = media[this.#mediaIndex] || media[0];
    const src = flavor.image || current?.thumb || current?.src;
    if (!src) return;
    this.refs.stickyThumb.src = src;
    this.refs.stickyThumb.alt = flavor.title;
  }

  #updateAtcState() {
    const flavor = this.#activeFlavor();
    const variant = this.#activeVariant();
    const notify = flavor ? this.#isNotifyMode(flavor) : false;
    const notifyPosted = Boolean(this.querySelector('[data-notify-success]'));
    let canAdd = Boolean(flavor?.hasProduct && variant?.id && variant.available);
    let label = canAdd
      ? this.#catalog?.settings.ctaLabel || 'Checkout'
      : this.#catalog?.settings.ctaDisabledLabel || 'Coming soon';

    if (notify) {
      canAdd = !notifyPosted;
      label = this.#catalog?.settings.notifyLabel || 'Notify Me When Available';
    } else if (this.#isBuilderMode()) {
      canAdd = this.#mixStatus === 'ready' && this.#totalMix() === this.#size && Boolean(variant?.id);
      label = this.#catalog?.settings.ctaLabel || 'Checkout';
    }

    if (this.refs.atcButton) {
      this.refs.atcButton.disabled = !canAdd;
      this.refs.atcButton.hidden = notify && (this.#notifyOpen || notifyPosted);
    }
    if (this.refs.atcLabel) this.refs.atcLabel.textContent = label;
    if (this.refs.stickyButton) {
      this.refs.stickyButton.disabled = !canAdd;
      const stickyLabel = this.refs.stickyButton.querySelector('[data-sticky-label]');
      if (stickyLabel) {
        stickyLabel.textContent = notify
          ? label
          : this.#catalog?.settings.stickyCta || label;
      }
    }
  }

  #updateSticky() {
    const flavor = this.#activeFlavor();
    const variant = this.#activeVariant();
    if (!variant || !flavor) return;
    const notify = this.#isNotifyMode(flavor);
    const display = this.#subscribe ? this.#planPrice(variant, this.#activePlan()) : variant.price;
    if (this.refs.stickyPrice) this.refs.stickyPrice.textContent = variant.price ? this.#formatMoney(display) : '';
    if (this.refs.stickyCompare) {
      const showCompare = this.#subscribe && variant.price > display && display > 0;
      this.refs.stickyCompare.textContent = showCompare ? this.#formatMoney(variant.price) : '';
      this.refs.stickyCompare.hidden = !showCompare;
    }
    if (this.refs.stickyTitle) {
      this.refs.stickyTitle.textContent = flavor.isBuilder ? 'Snack Buddies Mini Muffins' : flavor.title;
    }
    if (this.refs.stickyMeta) {
      if (notify) {
        this.refs.stickyMeta.textContent = 'Sold Out';
      } else {
        const sizeLabel = variant.title || `${variant.size} Boxes`;
        const planLabel = this.#subscribe
          ? `${this.#catalog?.settings.subscribeLabel || 'Subscribe & Save'} selected`
          : `${this.#catalog?.settings.oneTimeLabel || 'One-Time Purchase'} selected`;
        this.refs.stickyMeta.textContent = `${sizeLabel} · ${planLabel}`;
      }
    }
    if (this.refs.stickyEach) {
      const each =
        variant.size && display ? this.#formatMoney(this.#perBoxCents(display, variant.size)) : '';
      this.refs.stickyEach.textContent = each ? `${each}/box` : '';
    }
  }

  /**
   * @param {boolean} visible
   */
  #setStickyVisible(visible) {
    const sticky = this.refs.sticky;
    if (!sticky) return;
    sticky.classList.toggle('is-visible', visible);
    sticky.toggleAttribute('inert', !visible);
    sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (!visible) this.#setStickyFlavorsOpen(false);
    this.#syncFooterOffset();
  }

  #syncFooterOffset() {
    const sticky = this.refs.sticky;
    if (!(sticky instanceof HTMLElement)) return;
    const styles = getComputedStyle(sticky);
    const bottom = Number.parseFloat(styles.bottom) || 0;
    const height = sticky.getBoundingClientRect().height;
    const space = Math.max(0, Math.ceil(height + bottom + 16));
    document.documentElement.style.setProperty('--ctr-fp-sticky-offset', `${space}px`);
    document.documentElement.classList.add('has-ctr-fp-sticky');
  }

  #clearFooterOffset() {
    const others = [...document.querySelectorAll('ctr-fixed-pack-buybox')].some((el) => el !== this);
    if (others) return;
    document.documentElement.classList.remove('has-ctr-fp-sticky');
    document.documentElement.style.removeProperty('--ctr-fp-sticky-offset');
  }

  /**
   * @param {boolean} open
   */
  #setStickyFlavorsOpen(open) {
    this.#stickyFlavorsOpen = open;
    this.refs.stickyFlavors?.classList.toggle('is-open', open);
    const list = this.refs.stickyFlavorList;
    if (list) list.hidden = !open;
    this.refs.stickySelect?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * @param {Event} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#stickyFlavorsOpen) return;
    if (!(event.target instanceof Node)) return;
    if (this.refs.stickyFlavors?.contains(event.target)) return;
    this.#setStickyFlavorsOpen(false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#stickyFlavorsOpen) return;
    this.#setStickyFlavorsOpen(false);
    this.refs.stickySelect?.focus();
  };

  #observeSticky() {
    const sticky = this.refs.sticky;
    const section = this.closest('.ctr-fp-buybox-section');
    if (!sticky || !(section instanceof HTMLElement)) return;

    this.#setStickyVisible(false);
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = Boolean(entry) && !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.#setStickyVisible(scrolledPast);
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(section);
    this.#stickyResizeObserver = new ResizeObserver(() => this.#syncFooterOffset());
    this.#stickyResizeObserver.observe(sticky);
    this.#syncFooterOffset();
  }

  #isBuilderMode() {
    return Boolean(this.#activeFlavor()?.isBuilder);
  }

  /**
   * @param {CatalogFlavor} flavor
   */
  #isNotifyMode(flavor) {
    if (flavor.isBuilder || flavor.isVariety || !flavor.hasProduct) return false;
    const variant = flavor.variants.find((item) => item.size === this.#size) || flavor.variants[0];
    return Boolean(variant && !variant.available);
  }

  /**
   * @param {CatalogFlavor} flavor
   */
  #renderNotify(flavor) {
    const panel = this.refs.notifyPanel;
    const notify = this.#isNotifyMode(flavor);
    const posted = Boolean(this.querySelector('[data-notify-success]'));
    if (posted) this.#notifyOpen = true;
    if (panel) panel.hidden = !(notify && (this.#notifyOpen || posted));
    if (this.refs.atcButton) this.refs.atcButton.hidden = notify && (this.#notifyOpen || posted);
  }

  /**
   * @param {CatalogFlavor | undefined} flavor
   */
  #syncLadderHint(flavor) {
    const hint = this.refs.ladderHint;
    if (!(hint instanceof HTMLElement)) return;
    if (!flavor || !this.#isBuilderMode() || this.#isNotifyMode(flavor)) {
      hint.hidden = true;
      return;
    }

    const total = this.#totalMix();
    if (total < this.#size) {
      const remaining = this.#size - total;
      const nextText = this.#interpolate(
        this.#catalog?.settings.ladderAddMore || 'Add [remaining] more to complete your box',
        { remaining }
      );
      if (hint.textContent !== nextText) hint.textContent = nextText;
      hint.hidden = false;
      return;
    }

    const next = flavor.variants.find((item) => item.size > this.#size);
    if (!next) {
      hint.hidden = true;
      return;
    }

    const remaining = next.size - this.#size;
    const plan = this.#activePlan() || flavor.sellingPlans?.[0];
    const nextTotal =
      this.querySelector('[data-free-box-offer]') && plan ? this.#planPrice(next, plan) : next.price;
    const price = this.#formatMoney(this.#perBoxCents(nextTotal, next.size));
    const nextText = this.#interpolate(
      this.#catalog?.settings.ladderUnlock || '[remaining] more boxes unlocks [price]/box 🎉',
      { remaining, price }
    );
    if (hint.textContent !== nextText) hint.textContent = nextText;
    hint.hidden = false;
  }

  async #initMix() {
    const flavor = this.#activeFlavor();
    const mixGrid = this.refs.mixGrid;
    if (!flavor?.isBuilder || !(mixGrid instanceof HTMLElement)) return;

    this.#mixStatus = 'loading';
    if (this.refs.mixError) this.refs.mixError.hidden = true;

    const results = await Promise.allSettled(
      flavor.variants.map((variant) => this.#loadBox(variant.gid || `gid://shopify/ProductVariant/${variant.id}`))
    );

    const firstBox = results.find((result) => result.status === 'fulfilled')?.value;
    if (!firstBox) {
      this.#mixStatus = 'error';
      if (this.refs.mixError) this.refs.mixError.hidden = false;
      this.#updateAtcState();
      return;
    }

    this.#bindMixFlavors(firstBox);
    if (this.#totalMix() === 0) {
      const firstInStock = [...this.#mixMeta.entries()].find(([, meta]) => !meta.outOfStock);
      if (firstInStock) this.#mixSelections.set(firstInStock[0], this.#size);
    }
    this.#truncateMix();
    this.#mixStatus = 'ready';
    this.#updateMixUI();
    this.#updateAtcState();
    this.#updateSticky();
    this.#syncLadderHint(flavor);
  }

  /**
   * @param {string} variantGid
   */
  #loadBox(variantGid) {
    const cached = this.#boxCache.get(variantGid);
    if (cached) return cached;

    const numericId = variantGid.split('/').pop();
    const promise = fetch(SKIO_BOX_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productVariantPlatformId: numericId }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Skio responded with ${response.status}`);
        return response.json();
      })
      .then((json) => {
        const box = json?.ClassicBoxV2 ?? json?.box ?? json;
        if (!box || !Array.isArray(box.selectableProductVariants)) {
          throw new Error('Skio response is missing selectableProductVariants');
        }
        return box;
      });

    promise.catch(() => this.#boxCache.delete(variantGid));
    this.#boxCache.set(variantGid, promise);
    return promise;
  }

  /**
   * @param {{ selectableProductVariants: Array<{ platformId: string, productTitle: string, productVariantTitle: string, imageSrc?: string, outOfStock?: boolean }> }} box
   */
  #bindMixFlavors(box) {
    this.#mixMeta = new Map();
    const mixTiles = this.refs.mixGrid?.querySelectorAll('[data-flavor-row]') || [];
    const allRows = this.#mixRows();

    for (const skioFlavor of box.selectableProductVariants) {
      const tile = this.#findMixTile(skioFlavor, mixTiles);
      if (!(tile instanceof HTMLElement)) continue;
      this.#mixMeta.set(skioFlavor.platformId, skioFlavor);
      const handle = tile.dataset.flavorHandle || '';
      const label = tile.dataset.flavorLabel || '';
      for (const row of allRows) {
        if (
          (handle && row.dataset.flavorHandle === handle) ||
          (label && row.dataset.flavorLabel === label)
        ) {
          row.dataset.variantGid = skioFlavor.platformId;
          row.classList.toggle('is-sold-out', Boolean(skioFlavor.outOfStock));
        }
      }
    }

    for (const gid of [...this.#mixSelections.keys()]) {
      if (!this.#mixMeta.has(gid)) this.#mixSelections.delete(gid);
    }
  }

  /**
   * @param {{ productTitle: string }} skioFlavor
   * @param {NodeListOf<Element> | HTMLElement[]} tiles
   */
  #findMixTile(skioFlavor, tiles) {
    const title = (skioFlavor.productTitle || '').toLowerCase();
    for (const tile of tiles) {
      if (!(tile instanceof HTMLElement)) continue;
      const label = (tile.dataset.flavorLabel || '').toLowerCase();
      const handle = (tile.dataset.flavorHandle || '')
        .replace(/-copy$/, '')
        .replace(/^coming-soon-/, '')
        .replace(/-/g, ' ');
      if (label && title.includes(label)) return tile;
      if (handle && title.includes(handle)) return tile;
    }
    return null;
  }

  #updateMixUI() {
    const full = this.#totalMix() >= this.#size;

    for (const row of this.#mixRows()) {
      const gid = row.dataset.variantGid ?? '';
      const qty = this.#mixSelections.get(gid) ?? 0;
      const meta = gid ? this.#mixMeta.get(gid) : undefined;
      const soldOut = meta ? Boolean(meta.outOfStock) : row.classList.contains('is-sold-out');
      row.classList.toggle('is-sold-out', soldOut);
      const addButton = row.querySelector('[data-flavor-add]');
      const stepper = row.querySelector('[data-flavor-stepper]');
      const qtyLabel = row.querySelector('[data-flavor-qty]');
      const plusButton = row.querySelector('[data-flavor-plus]');
      const soldLabel = row.querySelector('[data-flavor-sold]');

      if (soldLabel instanceof HTMLElement) soldLabel.hidden = !soldOut;
      if (addButton instanceof HTMLElement) addButton.hidden = qty > 0 || soldOut;
      if (addButton instanceof HTMLButtonElement) addButton.disabled = full || soldOut || this.#mixStatus !== 'ready';
      if (stepper instanceof HTMLElement) stepper.hidden = qty === 0 || soldOut;
      if (qtyLabel) qtyLabel.textContent = String(qty);
      if (plusButton instanceof HTMLButtonElement) plusButton.disabled = full;
      row.classList.toggle('is-selected', qty > 0);
    }
    this.#syncExtraBox();
  }

  /**
   * @returns {HTMLElement[]}
   */
  #mixRows() {
    /** @type {HTMLElement[]} */
    const rows = [];
    for (const row of this.querySelectorAll('[data-flavor-row]')) {
      if (row instanceof HTMLElement) rows.push(row);
    }
    return rows;
  }

  #truncateMix() {
    while (this.#totalMix() > this.#size) {
      const lastGid = [...this.#mixSelections.keys()].pop();
      if (!lastGid) break;
      const qty = (this.#mixSelections.get(lastGid) ?? 0) - 1;
      if (qty <= 0) this.#mixSelections.delete(lastGid);
      else this.#mixSelections.set(lastGid, qty);
    }
  }

  #totalMix() {
    let total = 0;
    for (const qty of this.#mixSelections.values()) total += qty;
    return total;
  }

  /**
   * @param {Event} event
   */
  #mixRowGid(event) {
    if (!(event.target instanceof Element)) return undefined;
    const row = event.target.closest('[data-flavor-row]');
    return row instanceof HTMLElement ? row.dataset.variantGid : undefined;
  }

  toggleExtraBox() {
    const menu = this.querySelector('[data-extra-box-menu]');
    const toggle = this.querySelector('[data-extra-box-toggle]');
    if (!(menu instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;
    const open = menu.hidden;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * @param {Event} event
   */
  selectExtraBox(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-extra-option]') : null;
    if (!(option instanceof HTMLElement)) return;
    this.#extraTouched = true;
    this.#extraGid = option.dataset.extraOption || '';
    this.#paintExtraBox();
    const menu = this.querySelector('[data-extra-box-menu]');
    const toggle = this.querySelector('[data-extra-box-toggle]');
    if (menu instanceof HTMLElement) menu.hidden = true;
    if (toggle instanceof HTMLElement) toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {number} totalCents
   * @param {number} boxCount
   * @returns {number}
   */
  #perBoxCents(totalCents, boxCount) {
    if (this.querySelector('[data-free-box-offer]')) {
      return perBoxCentsFromTotal(totalCents, boxCount);
    }
    return Math.round(Number(totalCents) / Number(boxCount));
  }

  #extraBoxOnly() {
    return this.hasAttribute('data-extra-box-offer') && !this.querySelector('[data-free-box-offer]');
  }

  #syncExtraBox() {
    const row = this.querySelector('[data-extra-box]');
    if (!(row instanceof HTMLElement)) return;
    const show = this.#extraBoxOnly() && this.#size === 8;
    row.hidden = !show;
    if (!show) return;

    const choices = this.#extraChoices();
    const menu = row.querySelector('[data-extra-box-menu]');
    if (!(menu instanceof HTMLElement)) return;
    const signature = choices.map((choice) => choice.gid).join('|');
    if (menu.dataset.signature !== signature) {
      menu.dataset.signature = signature;
      menu.replaceChildren();
      for (const choice of choices) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ctr-extra-box__option';
        button.dataset.extraOption = choice.gid;
        button.setAttribute('role', 'option');
        const image = document.createElement('img');
        image.alt = '';
        image.width = 40;
        image.height = 40;
        if (choice.image) image.src = choice.image;
        else image.hidden = true;
        const label = document.createElement('span');
        label.textContent = choice.title;
        button.append(image, label);
        button.addEventListener('click', (event) => this.selectExtraBox(event));
        menu.append(button);
      }
    }

    const flavorKey = this.#activeFlavor()?.key || '';
    if (this.#extraFlavorKey !== flavorKey) {
      this.#extraFlavorKey = flavorKey;
      this.#extraTouched = false;
    }
    const order = choices.map((choice) => choice.gid);
    const quantities = new Map(choices.map((choice) => [choice.gid, choice.qty]));
    const kept = this.#extraTouched && (quantities.get(this.#extraGid) || 0) > 0;
    if (!kept) {
      this.#extraGid = majorityFlavor(quantities, order);
      this.#extraTouched = false;
    }
    this.#paintExtraBox(choices);
  }

  /**
   * @returns {{ gid: string, title: string, image: string, qty: number }[]}
   */
  #extraChoices() {
    if (this.#isBuilderMode()) {
      return [...this.#mixMeta.entries()].map(([gid, meta]) => ({
        gid,
        title: meta.productTitle || '',
        image: meta.imageSrc || '',
        qty: this.#mixSelections.get(gid) || 0,
      }));
    }

    const current = this.#activeFlavor();
    return (this.#catalog?.flavors || [])
      .filter((item) => item.oneBoxId && item.hasProduct && !item.isBuilder && !item.isVariety)
      .map((item) => ({
        gid: `gid://shopify/ProductVariant/${item.oneBoxId}`,
        title: item.label || item.title,
        image: item.image || '',
        qty: item.key === current?.key ? this.#size : 0,
      }));
  }

  /**
   * @param {{ gid: string, title: string, image: string }[]} [choices]
   */
  #paintExtraBox(choices = this.#extraChoices()) {
    const row = this.querySelector('[data-extra-box]');
    if (!(row instanceof HTMLElement)) return;
    const choice = choices.find((item) => item.gid === this.#extraGid);
    const name = row.querySelector('[data-extra-box-name]');
    const image = row.querySelector('[data-extra-box-image]');
    if (name) name.textContent = choice?.title || 'Select';
    if (image instanceof HTMLImageElement) {
      if (choice?.image) {
        image.src = choice.image;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }
    for (const option of row.querySelectorAll('[data-extra-option]')) {
      if (!(option instanceof HTMLElement)) continue;
      const selected = option.dataset.extraOption === this.#extraGid;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    }
  }

  /**
   * @param {{ items: Record<string, unknown>[] }} payload
   * @param {unknown} boxId
   */
  #appendExtraLine(payload, boxId) {
    if (!this.#extraBoxOnly() || this.#size !== 8) return;
    const extra = extraBoxLine(this.#extraGid);
    if (extra && extra.id !== Number(boxId)) {
      linkBonusBundle(payload.items[0], extra);
      payload.items.push(extra);
    }
  }

  async #addConfiguredBox() {
    const flavor = this.#activeFlavor();
    const variant = this.#activeVariant();
    if (!flavor || !variant?.id || this.#mixStatus !== 'ready' || this.#totalMix() !== this.#size) return;

    /** @type {Record<string, string | number | boolean>} */
    const properties = { _isClassicBoxV2: true };
    for (const [gid, qty] of this.#mixSelections) {
      const meta = this.#mixMeta.get(gid);
      if (!qty || !meta) continue;
      const hasVariantTitle = meta.productVariantTitle && meta.productVariantTitle !== 'Default Title';
      const humanKey = hasVariantTitle ? `${meta.productTitle} - ${meta.productVariantTitle}` : meta.productTitle;
      properties[humanKey] = qty;
      properties[`_pv${gid}`] = qty;
    }

    const scheduleOffer = Boolean(this.querySelector('[data-free-box-offer]'));
    if (this.#subscribe && scheduleOffer) Object.assign(properties, bonusLineProperties(this));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const line = {
      id: variant.id,
      quantity: 1,
      properties,
    };
    const includeFree = Boolean(scheduleOffer && this.#subscribe && this.#planId);
    if (this.#subscribe && this.#planId) line.selling_plan = this.#planId;
    const payload = subscriptionCartBody(line, this, includeFree, sectionIds.join(','), window.location.pathname);
    this.#appendExtraLine(payload, line.id);

    const { atcButton, stickyButton, atcError, liveRegion } = this.refs;
    atcButton.disabled = true;
    atcButton.setAttribute('aria-busy', 'true');
    if (stickyButton) {
      stickyButton.disabled = true;
      stickyButton.setAttribute('aria-busy', 'true');
    }
    if (atcError) atcError.hidden = true;

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify(payload) })
      ).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
        if (atcError) {
          atcError.textContent = response.message;
          atcError.hidden = false;
        }
        liveRegion.textContent = response.message;
        this.#errorTimeout = setTimeout(() => {
          if (atcError) atcError.hidden = true;
          liveRegion.textContent = '';
        }, 10000);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(variant.id), {
          source: 'ctr-fixed-pack-buybox',
          itemCount: payload.items.length,
          productId: String(flavor.key),
          variantId: String(variant.id),
          sections: response.sections,
        })
      );
      liveRegion.textContent = Theme.translations?.added ?? 'Added to cart';
    } catch (error) {
      console.error(error);
      if (atcError) {
        atcError.textContent = 'Something went wrong. Please try again.';
        atcError.hidden = false;
      }
    } finally {
      atcButton.removeAttribute('aria-busy');
      if (stickyButton) stickyButton.removeAttribute('aria-busy');
      this.#updateAtcState();
    }
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  #interpolate(template, values) {
    return template.replace(/\[(\w+)\]/g, (match, token) => (token in values ? String(values[token]) : match));
  }

  /**
   * @param {CatalogVariant} variant
   * @param {CatalogPlan | undefined} plan
   */
  #planPrice(variant, plan) {
    if (!plan) return variant.price;
    if (plan.value_type === 'percentage') {
      return Math.round((variant.price * (100 - Number(plan.value || 0))) / 100);
    }
    if (plan.value_type === 'fixed_amount') {
      return Math.max(0, variant.price - Number(plan.value || 0));
    }
    if (plan.value_type === 'price') {
      return Number(plan.value || variant.price);
    }
    return variant.price;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';
    if (!Number(cents)) return '';

    const template = this.refs.moneyFormat.content.textContent?.trim() || '{{ amount }}';
    const currency = this.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      const roundedNumber = (Number(cents) / 100).toFixed(precision);
      let [whole, fraction] = roundedNumber.split('.');
      if (!whole) whole = '0';
      if (!fraction) fraction = '';
      whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
      return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
    });
  }
}

if (!customElements.get('ctr-fixed-pack-buybox')) {
  customElements.define('ctr-fixed-pack-buybox', CtrFixedPackBuybox);
}
